// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

// Path & boolean geometry operations for the scenario editor.
//
// Boolean ops (Union / Intersection / Difference) run through polygon-clipping:
// every input shape is reduced to a single closed ring with its rotation baked
// in, the op is applied, and the OUTER rings of the result are returned as plain
// {x, y} point lists ready to become polygon elements.
//
// Limitation: the editor's polygon model is single-ring (no holes), so we keep
// only the outer ring of each result polygon. Holes would require a multi-ring
// path model (see the SVG-path foundation note in the optimization story).

import polygonClipping from "polygon-clipping";

// Rotate (x, y) by `deg` degrees around (cx, cy).
const rotatePoint = (x, y, cx, cy, deg) => {
  if (!deg) return [x, y];
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = x - cx;
  const dy = y - cy;
  return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
};

// Number of segments used to approximate a circle as a polygon ring.
const CIRCLE_SEGMENTS = 64;

// Resolve a polygon vertex to {x, y} (handles OSM lng/lat aliases).
const pointXY = (p) => ({
  x: p.x != null ? p.x : (p.lng ?? 0),
  y: p.y != null ? p.y : (p.lat ?? 0),
});

// Convert an editor element into a single closed ring ([[x,y], …, firstAgain])
// with rotation baked in. Returns null for non-geometry elements (text, pin).
// Detection is geometry-based so park objects (decorated rects/circles) work too.
export function elementToRing(el) {
  if (!el) return null;
  const rot = el.rotation || 0;

  // Polygon: rotate each vertex about the bounding-box centre (matches the
  // canvas transform origin).
  if (Array.isArray(el.points) && el.points.length >= 3) {
    const pts = el.points.map(pointXY);
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    const ring = pts.map((p) => rotatePoint(p.x, p.y, cx, cy, rot));
    ring.push(ring[0]);
    return ring;
  }

  // Circle (cx, cy, r) → regular polygon. Rotation is irrelevant for a circle.
  if (el.r != null && el.cx != null && el.cy != null) {
    const { cx, cy, r } = el;
    if (!(r > 0)) return null;
    const ring = [];
    for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
      const a = (i / CIRCLE_SEGMENTS) * 2 * Math.PI;
      ring.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
    }
    ring.push(ring[0]);
    return ring;
  }

  // Rect (x, y, width, height) → 4 corners rotated about the rect centre.
  if (el.width != null && el.height != null && el.x != null && el.y != null) {
    const { x, y, width: w, height: h } = el;
    if (!(w > 0) || !(h > 0)) return null;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const ring = [
      [x, y],
      [x + w, y],
      [x + w, y + h],
      [x, y + h],
    ].map(([px, py]) => rotatePoint(px, py, cx, cy, rot));
    ring.push(ring[0]);
    return ring;
  }

  return null;
}

// Apply a boolean op to ≥2 geometry elements. Returns an array of outer rings,
// each a list of {x, y} points (open — the closing duplicate is dropped),
// suitable for building polygon elements. Returns [] if fewer than 2 shapes
// reduce to rings or the op produces nothing.
export function booleanElements(op, els) {
  const polys = (els || [])
    .map(elementToRing)
    .filter(Boolean)
    .map((ring) => [ring]); // Polygon = Ring[]
  if (polys.length < 2) return [];

  const [first, ...rest] = polys;
  let result;
  if (op === "union") {
    result = polygonClipping.union(first, ...rest);
  } else if (op === "intersection") {
    result = polygonClipping.intersection(first, ...rest);
  } else if (op === "difference") {
    result = polygonClipping.difference(first, ...rest);
  } else {
    return [];
  }

  return result
    .map((poly) => {
      const outer = poly[0] || [];
      // Drop the closing vertex (polygon-clipping returns closed rings).
      const open =
        outer.length > 1 &&
        outer[0][0] === outer[outer.length - 1][0] &&
        outer[0][1] === outer[outer.length - 1][1]
          ? outer.slice(0, -1)
          : outer;
      return open.map(([x, y]) => ({ x, y }));
    })
    .filter((ring) => ring.length >= 3);
}

// Euclidean distance between two {x, y} points.
export function measureDistance(a, b) {
  return Math.hypot(b.x - a.x, b.y - a.y);
}
